import { Box, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { memo } from "react";
import { LocationsLoadingBucket } from "src/store/api/generatedApi";
import { TimelineSlot } from "./timelineLayout";

dayjs.extend(utc);

interface LocationsLoadingMarkersProps {
  buckets: readonly LocationsLoadingBucket[];
  totalLocations: number;
  slot: TimelineSlot;
  getPositionPercent: (time: Dayjs) => number;
}

export const LocationsLoadingMarkers = memo(
  ({
    buckets,
    totalLocations,
    slot,
    getPositionPercent,
  }: LocationsLoadingMarkersProps) => {
    if (totalLocations === 0) return null;
    return (
      <>
        {buckets
          .filter((bucket) => bucket.loading_location_names.length > 0)
          .map((bucket) => (
            <LocationsLoadingBucketMarker
              key={bucket.start}
              bucket={bucket}
              totalLocations={totalLocations}
              slot={slot}
              getPositionPercent={getPositionPercent}
            />
          ))}
      </>
    );
  }
);
LocationsLoadingMarkers.displayName = "LocationsLoadingMarkers";

const LocationsLoadingBucketMarker = memo(
  ({
    bucket,
    totalLocations,
    slot,
    getPositionPercent,
  }: {
    bucket: LocationsLoadingBucket;
    totalLocations: number;
    slot: TimelineSlot;
    getPositionPercent: (time: Dayjs) => number;
  }) => {
    const { t } = useTenantTranslation();
    const bucketStart = dayjs.utc(bucket.start);
    const bucketEnd = dayjs.utc(bucket.end);

    const leftPercent = Math.max(0, getPositionPercent(bucketStart));
    const widthPercent = Math.min(
      100 - leftPercent,
      getPositionPercent(bucketEnd) - leftPercent
    );

    const range = `${bucketStart.format("HH:mm")} – ${bucketEnd.format("HH:mm")} UTC`;
    const summary = t("locationsLoadingTooltipCount", {
      count: bucket.loading_location_names.length,
      total: totalLocations,
    });
    const tooltipTitle = `${range}\n${summary}\n${bucket.loading_location_names.join(", ")}`;

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Box
          sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 0.5)}%`,
            top: slot.top,
            height: slot.height,
            bgcolor: "data.green",
            borderRadius: slot.borderRadius,
            cursor: "inherit",
          }}
        />
      </Tooltip>
    );
  }
);
LocationsLoadingBucketMarker.displayName = "LocationsLoadingBucketMarker";
